import axios from 'axios';
import merge from 'lodash-es/merge';

import type { DateTime } from 'luxon';

/**
 * SurveysService
 */
export default class SurveysService
{
    /**
     * @param filter FilterModel
     * @param pager Pager
     *
     * @returns Promise<Pagination<Resource<ListItemModel>>>
     */
    public static async getList(filter: FilterModel, pager: Pager): Promise<Pagination<Resource<ListItemModel>>>
    {
        return (await axios.get<Pagination<Resource<ListItemModel>>>('admin/wk/surveys', {
            params: merge({}, filter, pager.data())
        })).data;
    }

    /**
     * @param id number
     *
     * @returns Promise<Resource<FormModel>>
     */
    public static async fetch(id: number): Promise<Resource<FormModel>>
    {
        return (await axios.get<Resource<FormModel>>(`admin/wk/surveys/${id}`)).data;
    }

    /**
     * @param model UpsertModel
     *
     * @returns Promise<Resource<FormModel>>
     */
    public static async create(model: UpsertModel): Promise<Resource<FormModel>>
    {
        return (await axios.post<Resource<FormModel>>(`admin/wk/surveys`, model)).data;
    }

    /**
     * @param id number
     * @param model UpsertModel
     *
     * @returns Promise<Resource<FormModel>>
     */
    public static async update(id: number, model: UpsertModel): Promise<Resource<FormModel>>
    {
        return (await axios.put<Resource<FormModel>>(`admin/wk/surveys/${id}`, model)).data;
    }

    /**
     * @param id number
     *
     * @returns Promise<Statement>
     */
    public static async remove(id: number): Promise<Statement>
    {
        return (await axios.delete(`admin/wk/surveys/${id}`)).data as Statement;
    }

    /**
     * @param id number
     * @param status PublicationEnum
     *
     * @returns Promise<Statement>
     */
    public static async status(id: number, status: PublicationEnum): Promise<Statement>
    {
        return (await axios.patch(`admin/wk/surveys/${id}/status`, {
            status: status
        })).data;
    }

    /**
     * @param id number
     * @param model PublishModel
     *
     * @returns Promise<Statement>
     */
    public static async publish(id: number, model: PublishModel): Promise<Statement>
    {
        return (await axios.patch(`admin/wk/surveys/${id}/publish`, model)).data;
    }

    /**
     * @param id number
     * @param model PublishModel
     *
     * @returns Promise<Statement>
     */
    public static async attach(id: number, pageId: number): Promise<Statement>
    {
        return (await axios.post(`admin/wk/surveys/${id}/page/${pageId}`)).data;
    }

    /**
     * @param id number
     * @param model PublishModel
     *
     * @returns Promise<Statement>
     */
    public static async detach(id: number, pageId: number): Promise<Statement>
    {
        return (await axios.delete(`admin/wk/surveys/${id}/page/${pageId}`)).data;
    }

    /**
     * @param id number
     *
     * @returns Promise<Pagination<EntityVersion>>
     */
    public static async getVersions(id: number, pager: Pager): Promise<Pagination<EntityVersion>>
    {
        return (await axios.get<Pagination<EntityVersion>>(`admin/wk/surveys/${id}/versions`, {
            params: pager.data()
        })).data;
    }

    /**
     * @param id number
     *
     * @returns Promise<PagePublication[]>
     */
    public static async getPagePublications(id: number): Promise<PagePublication[]>
    {
        return (await axios.get<PagePublication[]>(`admin/wk/surveys/${id}/pages`)).data;
    }

    /**
        * @param originId number
        *
        * @returns Promise<VoteListModel[]>
        */
    public static async results(originId: number): Promise<VoteListModel[]>
    {
        return (await axios.get<VoteListModel[]>(`admin/wk/surveys/results/${originId}`)).data;
    }

    public static async getReport(originId: number): Promise<Blob>
    {
        return (await axios.get<Blob>(`admin/wk/surveys/report/${originId}`, { responseType: 'blob' })).data;
    }

    /**
     * @param search string
     * @param limit number
     * @param except number[]
     *
     * @returns Promise<ListItemModel[]>
     */
    public static async getAutocompleteList(search: string, limit: number = 10, except: number[] = []): Promise<ListItemModel[]>
    {
        return (await axios.get<ListItemModel[]>(`admin/wk/surveys/autocomplete`, {
            params: {
                search: search,
                limit: limit,
                except: except
            }
        })).data;
    }

    /**
    * @param id number
    *
    * @returns Promise<ListItemModel>
    */
    public static async fetchAutocompleteModel(id: number): Promise<ListItemModel>
    {
        return (await axios.get<ListItemModel>(`admin/wk/surveys/autocomplete/${id}`)).data;
    }

    /**
        * @param originId number
        *
        * @returns Promise<ListItemModel>
        */
    public static async fetchAutocompleteModelByOriginId(originId: number): Promise<ListItemModel>
    {
        return (await axios.get<ListItemModel>(`admin/wk/surveys/autocomplete/${originId}/origin`)).data;
    }

    /**
        * @param id number
        *
        * @returns Promise<SurveySummaryModel>
        */
    public static async fetchSurveySummary(id: number): Promise<SurveySummaryModel>
    {
        return (await axios.get<SurveySummaryModel>(`wk/dashboards/widgets/survey/${id}`)).data;
    }

    /**
     * @param articleId number
     *
     * @returns Promise<FormModel[]>
     */
    public static async getListByArticleId(articleId: number): Promise<ListItemModel[]>
    {
        return (await axios.get<ListItemModel[]>(`admin/wk/surveys/by-article/${articleId}`)).data;
    }
}

export interface ListItemModel
{
    id: number;
    originId: number;
    dateCreatedUtc: DateTime;
    dateModifiedUtc: DateTime;
    createdBy: string;
    modifiedBy: string;
    question: string;
    status: PublicationEnum;
    revisionOrigin: number;
    ownerId: number;
    resourceId: number;
    answersCount: number;
}

export interface FilterModel
{
    pageId?: number;
    userId: number;
    status: string;
    question: string;
    dateFromUtc?: DateTime;
    dateDueUtc?: DateTime;
    onlyMine: boolean;
    onlyTeam: boolean;
    isActive: boolean;
}

export interface UpsertModel
{
    id: number;
    question: string;
    answers: SurveyAnswer[];
    meta: MetaSettingsModel | null;
    hideResults: boolean;
    articleId?: number;
}

export interface SurveyAnswer
{
    id: number;
    text: string;
}

export interface FormModel extends UpsertModel
{
    datePublishedUtc?: DateTime;
    dateDismissedUtc?: DateTime;
    status: PublicationEnum;
}

export interface PublishModel
{
    datePublishedUtc?: DateTime;
    dateDismissedUtc?: DateTime;
}

export interface VoteListModel
{
    answerId: number;
    answerName: string;
    percent: number;
    votes: number;
}

export interface SurveySummaryModel
{
    id: number;
    question: string;
    answers: SurveyAnswerModel;
}

export interface SurveyAnswerModel
{
    id: number;
    answer: string;
    count: number;
}

export interface SurveySummaryList
{
    survey: SurveySummaryModel;
}
